import React, { createContext, useContext, useState, useCallback } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import LoginPage from "./components/LoginPage";
import SignupPage from "./components/SignupPage";
import ProfilePage from "./components/ProfilePage";
import MlmTree from "./components/MlmTree";
import MemberSubtree from "./components/MemberSubtree";
import DashboardPage from "./components/DashboardPage";
import TeamMembersPage from "./components/TeamMembersPage";
import CommissionsPage from "./components/CommissionsPage";
import AppLayout from "./components/AppLayout";
import PostIncentivesPage from "./components/PostIncentivesPage";
import { authenticate, registerUser } from "./script/rpc";
import { setCookie, getCookie } from "./script/cookies";

/* ═══════════════════════════════════════════════════════
   Auth Context — stores uid + password, persists to
   sessionStorage so a page refresh keeps you logged in.
   ═══════════════════════════════════════════════════════ */
const AuthContext = createContext(null);

function loadSession() {
  try {
    const raw = sessionStorage.getItem("mlm_auth");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => loadSession());

  const login = useCallback((uid, password) => {
    const data = { uid, password };
    setAuth(data);
    sessionStorage.setItem("mlm_auth", JSON.stringify(data));
  }, []);

  const logout = useCallback(() => {
    setAuth(null);
    sessionStorage.removeItem("mlm_auth");
  }, []);

  return (
    <AuthContext.Provider value={{ auth, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }

/* ═══════════════════════════════════════════════════════
   Protected Route — redirects to /login if not authed
   ═══════════════════════════════════════════════════════ */
function ProtectedRoute({ children }) {
  const { auth } = useAuth();
  return auth ? children : <Navigate to="/login" replace />;
}

/* ═══════════════════════════════════════════════════════
   Login wrapper
   ═══════════════════════════════════════════════════════ */
function LoginRoute() {
  const { auth, login } = useAuth();
  const navigate = useNavigate();

  if (auth) return <Navigate to="/dashboard" replace />;

  return (
    <LoginPage
      onLogin={(uid, password) => {
        login(uid, password);
        navigate("/dashboard", { replace: true });
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════
   Signup wrapper
   ═══════════════════════════════════════════════════════ */
function SignupRoute() {
  const { auth, login } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  if (auth) return <Navigate to={`/edit-profile${search}`} replace />;

  const handleSignup = async (name, username, password) => {
    try {
      await registerUser(name, username, password);
    } catch (err) {
      throw new Error("Email address already exists..");
    }
    try {
      const uid = await authenticate(username, password);
      login(uid, password);
      navigate(`/edit-profile${search}`, { replace: true });
    } catch (err) {
      throw new Error("User created, but login failed: " + err.message);
    }
  };

  return <SignupPage onSignup={handleSignup} />;
}

/* ═══════════════════════════════════════════════════════
   ProfilePage — standalone page outside AppLayout
   ═══════════════════════════════════════════════════════ */
function ProfileRoute() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  return <ProfilePage uid={auth.uid} password={auth.password} onLogout={handleLogout} />;
}

/* ═══════════════════════════════════════════════════════
   Referral Tracker — intercepts URL params
   ═══════════════════════════════════════════════════════ */
function ReferralTracker() {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const params = new URLSearchParams(search);
    const ref = params.get("referral");
    if (ref) {
      setCookie("mlm_referral_code", ref, 30);
      // If we land on root or login with a referral, go to signup
      if (pathname === "/" || pathname === "/login") {
        navigate(`/signup${search}`, { replace: true });
      }
    }
  }, [search, pathname, navigate]);

  return null;
}

/* ═══════════════════════════════════════════════════════
   Root App
   ═══════════════════════════════════════════════════════ */
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastContainer position="top-right" autoClose={3000} />
        <ReferralTracker />
        <Routes>
          {/* Default */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Public */}
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupRoute />} />

          {/* Edit Profile — standalone (has its own full-page layout) */}
          <Route
            path="/edit-profile"
            element={
              <ProtectedRoute>
                <ProfileRoute />
              </ProtectedRoute>
            }
          />

          {/* ── Protected pages inside shared AppLayout (one sidebar) ── */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage section="dashboard" />} />
            <Route path="/transactions" element={<DashboardPage section="transactions" />} />
            <Route path="/tx_new" element={<DashboardPage section="tx_new" />} />
            <Route path="/profile" element={<DashboardPage section="profile" />} />
            <Route path="/team" element={<TeamMembersPage />} />
            <Route path="/commissions" element={<CommissionsPage />} />
            <Route path="/tree" element={<MlmTree />} />
            <Route path="/tree/:memberId" element={<MemberSubtree />} />
            <Route path="/incentives" element={<PostIncentivesPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}